import {
  createPostgresPersistence,
  effectDocumentPersistenceFromPromise,
  sqlRunnerFromPgPool,
  type DocumentPersistence,
  type DocumentPersistenceError,
  type PgPoolLike,
} from '@okrapdf/core/persistence';
import {
  Context,
  Data,
  Effect,
  Exit,
  FileSystem,
  Layer,
  ManagedRuntime,
  Path,
  Schema,
} from 'effect';

import { nodeServicesLayer } from '../node-platform.js';

import type {
  MaterializedOriginal,
  PersistenceServeStoreOptions,
  ResolveServeStoreOptions,
  ServePgPoolFactory,
  ServeStore,
  StarterDocumentGraph,
} from './serve-store.js';
import { SelfHostResourceIdSchema } from './self-host-resource-id.js';

const TypeId = 'okrapdf/cli/ServeStore/TypeId' as const;

function isGraph(value: unknown): value is StarterDocumentGraph {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { schema_version?: unknown }).schema_version === 'okra-document-graph/v1'
  );
}

export class ServeStoreError extends Data.TaggedError('ServeStoreError')<{
  readonly message: string;
  readonly method: string;
  readonly documentId?: string;
  readonly cause?: unknown;
}> {}

export interface EffectServeStore {
  readonly [TypeId]: typeof TypeId;
  readonly backend: ServeStore['backend'];
  readonly ready: () => Effect.Effect<void, ServeStoreError>;
  readonly readGraph: (
    documentId: string,
  ) => Effect.Effect<StarterDocumentGraph | null, ServeStoreError>;
  readonly writeGraph: (
    graph: StarterDocumentGraph,
  ) => Effect.Effect<StarterDocumentGraph, ServeStoreError>;
  readonly listGraphs: () => Effect.Effect<ReadonlyArray<StarterDocumentGraph>, ServeStoreError>;
  readonly readOriginal: (documentId: string) => Effect.Effect<Uint8Array | null, ServeStoreError>;
  readonly writeOriginal: (
    documentId: string,
    bytes: Uint8Array,
  ) => Effect.Effect<void, ServeStoreError>;
  readonly withMaterializedOriginal: <A, E>(
    documentId: string,
    use: (original: MaterializedOriginal) => Effect.Effect<A, E>,
  ) => Effect.Effect<A, ServeStoreError | E>;
  readonly acquireLease?: (
    documentId: string,
    instanceId: string,
    ttlMs: number,
  ) => Effect.Effect<boolean, ServeStoreError>;
  readonly releaseLease?: (
    documentId: string,
    instanceId: string,
  ) => Effect.Effect<void, ServeStoreError>;
  readonly close: () => Effect.Effect<void, ServeStoreError>;
}

export const ServeStoreService: Context.Service<EffectServeStore, EffectServeStore> =
  Context.Service('okrapdf/cli/ServeStore');

function failureMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function storeFailure(
  method: string,
  documentId: string | undefined,
  cause: unknown,
): ServeStoreError {
  return new ServeStoreError({
    method,
    documentId,
    message: failureMessage(cause),
    cause,
  });
}

function lift<A>(
  method: string,
  documentId: string | undefined,
  run: () => Promise<A>,
): Effect.Effect<A, ServeStoreError> {
  return Effect.tryPromise({
    try: run,
    catch: (cause) => storeFailure(method, documentId, cause),
  });
}

function liftPersistence<A>(
  method: string,
  documentId: string | undefined,
  effect: Effect.Effect<A, DocumentPersistenceError>,
): Effect.Effect<A, ServeStoreError> {
  return effect.pipe(Effect.mapError((error) => storeFailure(method, documentId, error.cause)));
}

/** Lift the existing promise ServeStore behind the Effect requirement. */
export function fromPromiseStore(store: ServeStore): EffectServeStore {
  return ServeStoreService.of({
    [TypeId]: TypeId,
    backend: store.backend,
    ready: () => lift('ready', undefined, () => store.ready()),
    readGraph: (documentId) => lift('readGraph', documentId, () => store.readGraph(documentId)),
    writeGraph: (graph) => lift('writeGraph', graph.document.id, () => store.writeGraph(graph)),
    listGraphs: () => lift('listGraphs', undefined, () => store.listGraphs()),
    readOriginal: (documentId) =>
      lift('readOriginal', documentId, () => store.readOriginal(documentId)),
    writeOriginal: (documentId, bytes) =>
      lift('writeOriginal', documentId, () => store.writeOriginal(documentId, bytes)),
    withMaterializedOriginal: (documentId, use) =>
      lift('withMaterializedOriginal', documentId, () =>
        store.withMaterializedOriginal(documentId, (original) => Effect.runPromise(use(original))),
      ),
    ...(store.acquireLease
      ? {
          acquireLease: (documentId: string, instanceId: string, ttlMs: number) =>
            lift('acquireLease', documentId, () =>
              store.acquireLease!(documentId, instanceId, ttlMs),
            ),
        }
      : {}),
    ...(store.releaseLease
      ? {
          releaseLease: (documentId: string, instanceId: string) =>
            lift('releaseLease', documentId, () => store.releaseLease!(documentId, instanceId)),
        }
      : {}),
    close: () => lift('close', undefined, () => store.close()),
  });
}

function liftFilesystem<A, E, R>(
  method: string,
  documentId: string | undefined,
  effect: Effect.Effect<A, E, R>,
): Effect.Effect<A, ServeStoreError, R> {
  return Effect.mapError(effect, (cause) => storeFailure(method, documentId, cause));
}

/** Filesystem store over Effect's portable FileSystem + Path services. */
export function layerFilesystem(
  dataDir: string,
): Layer.Layer<EffectServeStore, never, FileSystem.FileSystem | Path.Path> {
  return Layer.effect(
    ServeStoreService,
    Effect.gen(function* () {
      const fileSystem = yield* FileSystem.FileSystem;
      const paths = yield* Path.Path;
      const graphPath = (documentId: string) =>
        paths.join(dataDir, 'documents', documentId, 'graph.json');
      const originalPath = (documentId: string) =>
        paths.join(dataDir, 'documents', documentId, 'original.pdf');

      const readGraph = (documentId: string) => {
        const path = graphPath(documentId);
        return liftFilesystem(
          'readGraph',
          documentId,
          Effect.gen(function* () {
            if (!(yield* fileSystem.exists(path))) return null;
            const content = yield* fileSystem.readFileString(path);
            const value = yield* Effect.try(() => JSON.parse(content) as unknown);
            return isGraph(value) ? value : null;
          }),
        );
      };

      return make({
        backend: 'filesystem',
        ready: () => Effect.succeed(undefined),
        readGraph,
        writeGraph: (graph) => {
          const path = graphPath(graph.document.id);
          return liftFilesystem(
            'writeGraph',
            graph.document.id,
            Effect.gen(function* () {
              const content = yield* Effect.try(() => `${JSON.stringify(graph, null, 2)}\n`);
              yield* fileSystem.makeDirectory(paths.dirname(path), { recursive: true });
              yield* fileSystem.writeFileString(path, content);
              return graph;
            }),
          );
        },
        listGraphs: () =>
          liftFilesystem(
            'listGraphs',
            undefined,
            Effect.gen(function* () {
              const directory = paths.join(dataDir, 'documents');
              if (!(yield* fileSystem.exists(directory))) return [];
              const entries = yield* fileSystem.readDirectory(directory);
              const graphs = yield* Effect.forEach(entries, readGraph);
              return graphs.filter((graph): graph is StarterDocumentGraph => graph !== null);
            }),
          ),
        readOriginal: (documentId) => {
          const path = originalPath(documentId);
          return liftFilesystem(
            'readOriginal',
            documentId,
            Effect.gen(function* () {
              if (!(yield* fileSystem.exists(path))) return null;
              return yield* fileSystem.readFile(path);
            }),
          );
        },
        writeOriginal: (documentId, bytes) => {
          const path = originalPath(documentId);
          return liftFilesystem(
            'writeOriginal',
            documentId,
            Effect.gen(function* () {
              yield* fileSystem.makeDirectory(paths.dirname(path), { recursive: true });
              yield* fileSystem.writeFile(path, bytes);
            }),
          );
        },
        withMaterializedOriginal: (documentId, use) => use({ dataDir, documentId }),
        close: () => Effect.succeed(undefined),
      });
    }),
  );
}

function emptyDocument(documentId: string, ownerUserId: string) {
  return {
    id: documentId,
    ownerUserId,
    orgId: null,
    projectId: null,
    isPublished: false,
    spec: {},
    meta: {},
    graph: {},
    phase: 'idle',
  };
}

function makePersistenceStore(
  persistence: DocumentPersistence,
  ownerUserId: string,
  fileSystem: FileSystem.FileSystem,
  paths: Path.Path,
  closeEffect?: Effect.Effect<void, ServeStoreError>,
): EffectServeStore {
  const native = effectDocumentPersistenceFromPromise(persistence);
  const ensureDocument = (documentId: string, method: string) =>
    Effect.gen(function* () {
      const existing = yield* liftPersistence(
        method,
        documentId,
        native.getDocumentEffect(documentId),
      );
      if (!existing) {
        yield* liftPersistence(
          method,
          documentId,
          native.putDocumentEffect(emptyDocument(documentId, ownerUserId)),
        );
      }
    });

  const readOriginal = (documentId: string) =>
    liftPersistence(
      'readOriginal',
      documentId,
      native
        .getBlobEffect(documentId, 'source', 'source.pdf')
        .pipe(Effect.map((blob) => (blob ? new Uint8Array(blob.bytes) : null))),
    );

  return make({
    backend: 'postgres',
    ready: () => Effect.void,
    readGraph: (documentId) =>
      liftPersistence(
        'readGraph',
        documentId,
        native
          .getDocumentEffect(documentId)
          .pipe(Effect.map((document) => (isGraph(document?.graph) ? document.graph : null))),
      ),
    writeGraph: (graph) =>
      liftPersistence(
        'writeGraph',
        graph.document.id,
        native
          .putDocumentEffect({
            id: graph.document.id,
            ownerUserId,
            orgId: null,
            projectId: null,
            isPublished: false,
            spec: {},
            meta: {},
            graph: graph as unknown as Record<string, unknown>,
            phase: graph.document.lifecycle_state,
          })
          .pipe(Effect.as(graph)),
      ),
    listGraphs: () =>
      liftPersistence(
        'listGraphs',
        undefined,
        native.listDocumentsByOwnerEffect(ownerUserId).pipe(
          Effect.map((documents) =>
            documents.flatMap((document) => {
              const graph = isGraph(document.graph) ? document.graph : null;
              return graph ? [graph] : [];
            }),
          ),
        ),
      ),
    readOriginal,
    writeOriginal: (documentId, bytes) =>
      Effect.gen(function* () {
        yield* ensureDocument(documentId, 'writeOriginal');
        yield* liftPersistence(
          'writeOriginal',
          documentId,
          native.putBlobEffect({
            documentId,
            kind: 'source',
            name: 'source.pdf',
            contentType: 'application/pdf',
            bytes,
          }),
        );
      }),
    withMaterializedOriginal: (documentId, use) =>
      Effect.scoped(
        Effect.gen(function* () {
          const materializedDataDir = yield* liftFilesystem(
            'withMaterializedOriginal',
            documentId,
            fileSystem.makeTempDirectoryScoped({ prefix: 'okrapdf-serve-original-' }),
          );
          const materializedDocumentId = 'document';
          const bytes = yield* readOriginal(documentId);
          if (bytes) {
            const path = paths.join(
              materializedDataDir,
              'documents',
              materializedDocumentId,
              'original.pdf',
            );
            yield* liftFilesystem(
              'withMaterializedOriginal',
              documentId,
              Effect.gen(function* () {
                yield* fileSystem.makeDirectory(paths.dirname(path), { recursive: true });
                yield* fileSystem.writeFile(path, bytes);
              }),
            );
          }
          return yield* use({
            dataDir: materializedDataDir,
            documentId: materializedDocumentId,
          });
        }),
      ),
    acquireLease: (documentId, instanceId, ttlMs) =>
      Effect.gen(function* () {
        yield* ensureDocument(documentId, 'acquireLease');
        return yield* liftPersistence(
          'acquireLease',
          documentId,
          native.acquireLeaseEffect(documentId, instanceId, ttlMs),
        );
      }),
    releaseLease: (documentId, instanceId) =>
      liftPersistence(
        'releaseLease',
        documentId,
        native.releaseLeaseEffect(documentId, instanceId),
      ),
    close: () => closeEffect ?? liftPersistence('close', undefined, native.closeEffect()),
  });
}

/** Native document/blob/lease store over any core persistence implementation. */
export function layerPersistence(
  persistence: DocumentPersistence,
  options: PersistenceServeStoreOptions = {},
): Layer.Layer<EffectServeStore, never, FileSystem.FileSystem | Path.Path> {
  return Layer.effect(
    ServeStoreService,
    Effect.gen(function* () {
      const fileSystem = yield* FileSystem.FileSystem;
      const paths = yield* Path.Path;
      return makePersistenceStore(
        persistence,
        options.ownerUserId?.trim() || 'self-host',
        fileSystem,
        paths,
      );
    }),
  );
}

function missingPgError(cause: unknown): Error {
  return new Error(
    'DATABASE_URL is set, but the optional "pg" package is unavailable. Install pg in the okraPDF host (for example, `npm install pg`) and restart `okra serve`.',
    { cause },
  );
}

function isMissingModuleError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const code = (error as Error & { code?: string }).code;
  return (
    code === 'ERR_MODULE_NOT_FOUND' ||
    code === 'MODULE_NOT_FOUND' ||
    /cannot find (?:package|module).*\bpg\b/i.test(error.message)
  );
}

async function createDefaultPgPool(databaseUrl: string): Promise<PgPoolLike> {
  try {
    const { Pool } = await import('pg');
    return new Pool({ connectionString: databaseUrl }) as unknown as PgPoolLike;
  } catch (error) {
    throw missingPgError(error);
  }
}

function closePgPool(pool: PgPoolLike): Effect.Effect<void, ServeStoreError> {
  return Effect.tryPromise({
    try: () => pool.end(),
    catch: (cause) => storeFailure('close', undefined, cause),
  });
}

const acquirePostgresStore = Effect.fn('okrapdf/cli.acquirePostgresServeStore')(function* (
  databaseUrl: string,
  ownerUserId: string,
  poolFactory: ServePgPoolFactory,
  fileSystem: FileSystem.FileSystem,
  paths: Path.Path,
) {
  let closeOnce: Effect.Effect<void, ServeStoreError> | undefined;
  const pool = yield* Effect.acquireRelease(
    Effect.tryPromise({
      try: () => Promise.resolve(poolFactory(databaseUrl)),
      catch: (cause) => {
        const resolved = isMissingModuleError(cause) ? missingPgError(cause) : cause;
        return storeFailure('ready', undefined, resolved);
      },
    }),
    (acquired) => Effect.suspend(() => closeOnce ?? closePgPool(acquired)).pipe(Effect.ignore),
  );
  closeOnce = yield* Effect.cached(closePgPool(pool));
  const persistence = yield* Effect.try({
    try: () => createPostgresPersistence(sqlRunnerFromPgPool(pool)),
    catch: (cause) => storeFailure('ready', undefined, cause),
  });
  yield* liftPersistence('ready', undefined, persistence.migrateEffect());
  return makePersistenceStore(persistence, ownerUserId, fileSystem, paths, closeOnce);
});

function layerPostgres(
  databaseUrl: string,
  ownerUserId: string,
  poolFactory: ServePgPoolFactory,
): Layer.Layer<EffectServeStore, ServeStoreError, FileSystem.FileSystem | Path.Path> {
  return Layer.effect(
    ServeStoreService,
    Effect.gen(function* () {
      const fileSystem = yield* FileSystem.FileSystem;
      const paths = yield* Path.Path;
      return yield* acquirePostgresStore(databaseUrl, ownerUserId, poolFactory, fileSystem, paths);
    }),
  );
}

/** Select one scoped backend layer. Acquisition, migration, and close share the layer scope. */
export function layerFromResolve(
  options: ResolveServeStoreOptions,
): Layer.Layer<EffectServeStore, ServeStoreError> {
  const databaseUrl = options.env.DATABASE_URL?.trim();
  if (!databaseUrl) {
    return layerFilesystem(options.dataDir).pipe(Layer.provide(nodeServicesLayer));
  }
  return layerPostgres(
    databaseUrl,
    options.env.OKRA_SELF_HOST_OWNER?.trim() || 'self-host',
    options.poolFactory ?? createDefaultPgPool,
  ).pipe(Layer.provide(nodeServicesLayer));
}

/**
 * Preserve the synchronous service factory while one ManagedRuntime owns the
 * selected layer and its resource scope for the lifetime of the returned view.
 */
export function resolveEffectServeStore(options: ResolveServeStoreOptions): EffectServeStore {
  const backend: EffectServeStore['backend'] = options.env.DATABASE_URL?.trim()
    ? 'postgres'
    : 'filesystem';
  const runtime = ManagedRuntime.make(layerFromResolve(options));
  let initialized = false;
  let closePromise: Promise<Exit.Exit<void, ServeStoreError>> | undefined;

  const run = <A, E>(
    effect: Effect.Effect<A, E, EffectServeStore>,
  ): Effect.Effect<A, E | ServeStoreError> =>
    Effect.promise(() => runtime.runPromiseExit(effect)).pipe(Effect.flatMap((exit) => exit));

  const withStore = <A, E>(
    use: (store: EffectServeStore) => Effect.Effect<A, E>,
  ): Effect.Effect<A, E | ServeStoreError> =>
    run(
      Effect.flatMap(ServeStoreService, (store) =>
        Effect.sync(() => {
          initialized = true;
        }).pipe(Effect.andThen(use(store))),
      ),
    );

  const close = (): Effect.Effect<void, ServeStoreError> =>
    Effect.promise(() => {
      closePromise ??= (async () => {
        if (!initialized) {
          await runtime.dispose();
          return Exit.void;
        }
        const exit = await runtime.runPromiseExit(
          Effect.flatMap(ServeStoreService, (store) => store.close()),
        );
        await runtime.dispose();
        return exit;
      })();
      return closePromise;
    }).pipe(Effect.flatMap((exit) => exit));

  return make({
    backend,
    ready: () => withStore(() => Effect.void),
    readGraph: (documentId) => withStore((store) => store.readGraph(documentId)),
    writeGraph: (graph) => withStore((store) => store.writeGraph(graph)),
    listGraphs: () => withStore((store) => store.listGraphs()),
    readOriginal: (documentId) => withStore((store) => store.readOriginal(documentId)),
    writeOriginal: (documentId, bytes) =>
      withStore((store) => store.writeOriginal(documentId, bytes)),
    withMaterializedOriginal: (documentId, use) =>
      withStore((store) => store.withMaterializedOriginal(documentId, use)),
    ...(backend === 'postgres'
      ? {
          acquireLease: (documentId: string, instanceId: string, ttlMs: number) =>
            withStore((store) => store.acquireLease!(documentId, instanceId, ttlMs)),
          releaseLease: (documentId: string, instanceId: string) =>
            withStore((store) => store.releaseLease!(documentId, instanceId)),
        }
      : {}),
    close,
  });
}

export interface ServeStoreMakeOptions {
  readonly backend: ServeStore['backend'];
  readonly ready: () => Effect.Effect<void, ServeStoreError>;
  readonly readGraph: (
    documentId: string,
  ) => Effect.Effect<StarterDocumentGraph | null, ServeStoreError>;
  readonly writeGraph: (
    graph: StarterDocumentGraph,
  ) => Effect.Effect<StarterDocumentGraph, ServeStoreError>;
  readonly listGraphs: () => Effect.Effect<ReadonlyArray<StarterDocumentGraph>, ServeStoreError>;
  readonly readOriginal: (documentId: string) => Effect.Effect<Uint8Array | null, ServeStoreError>;
  readonly writeOriginal: (
    documentId: string,
    bytes: Uint8Array,
  ) => Effect.Effect<void, ServeStoreError>;
  readonly withMaterializedOriginal: EffectServeStore['withMaterializedOriginal'];
  readonly acquireLease?: EffectServeStore['acquireLease'];
  readonly releaseLease?: EffectServeStore['releaseLease'];
  readonly close: () => Effect.Effect<void, ServeStoreError>;
}

/** Construct an EffectServeStore from the store primitives. */
export function make(options: ServeStoreMakeOptions): EffectServeStore {
  const validateDocumentId = (method: string, documentId: string) =>
    Schema.decodeUnknownEffect(SelfHostResourceIdSchema)(documentId).pipe(
      Effect.mapError((cause) => storeFailure(method, documentId, cause)),
    );

  return ServeStoreService.of({
    [TypeId]: TypeId,
    backend: options.backend,
    ready: options.ready,
    readGraph: (documentId) =>
      validateDocumentId('readGraph', documentId).pipe(
        Effect.flatMap(() => options.readGraph(documentId)),
      ),
    writeGraph: (graph) =>
      validateDocumentId('writeGraph', graph.document.id).pipe(
        Effect.flatMap(() => options.writeGraph(graph)),
      ),
    listGraphs: options.listGraphs,
    readOriginal: (documentId) =>
      validateDocumentId('readOriginal', documentId).pipe(
        Effect.flatMap(() => options.readOriginal(documentId)),
      ),
    writeOriginal: (documentId, bytes) =>
      validateDocumentId('writeOriginal', documentId).pipe(
        Effect.flatMap(() => options.writeOriginal(documentId, bytes)),
      ),
    withMaterializedOriginal: (documentId, use) =>
      validateDocumentId('withMaterializedOriginal', documentId).pipe(
        Effect.flatMap(() => options.withMaterializedOriginal(documentId, use)),
      ),
    ...(options.acquireLease
      ? {
          acquireLease: (documentId: string, instanceId: string, ttlMs: number) =>
            validateDocumentId('acquireLease', documentId).pipe(
              Effect.flatMap(() => options.acquireLease!(documentId, instanceId, ttlMs)),
            ),
        }
      : {}),
    ...(options.releaseLease
      ? {
          releaseLease: (documentId: string, instanceId: string) =>
            validateDocumentId('releaseLease', documentId).pipe(
              Effect.flatMap(() => options.releaseLease!(documentId, instanceId)),
            ),
        }
      : {}),
    close: options.close,
  });
}

/** Process-local ServeStore for tests and ephemeral runtimes. */
export const layerMemory: Layer.Layer<EffectServeStore> = Layer.sync(ServeStoreService, () => {
  const graphs = new Map<string, StarterDocumentGraph>();
  const originals = new Map<string, Uint8Array>();
  return make({
    backend: 'filesystem',
    ready: () => Effect.succeed(undefined),
    readGraph: (documentId) => Effect.sync(() => graphs.get(documentId) ?? null),
    writeGraph: (graph) =>
      Effect.sync(() => {
        graphs.set(graph.document.id, graph);
        return graph;
      }),
    listGraphs: () => Effect.sync(() => [...graphs.values()]),
    readOriginal: (documentId) =>
      Effect.sync(() => {
        const bytes = originals.get(documentId);
        return bytes === undefined ? null : new Uint8Array(bytes);
      }),
    writeOriginal: (documentId, bytes) =>
      Effect.sync(() => {
        originals.set(documentId, new Uint8Array(bytes));
      }),
    withMaterializedOriginal: (documentId, use) =>
      use({ dataDir: 'memory://serve-store', documentId }),
    close: () => Effect.succeed(undefined),
  });
});

function assertContract(condition: boolean, message: string): asserts condition {
  if (!condition) throw new Error(message);
}

/** Layer-parameterized ServeStore contract. Every backend must pass the same assertions. */
export async function serveStoreContractSuite<E>(
  name: string,
  layer: Layer.Layer<EffectServeStore, E>,
): Promise<void> {
  const graph: StarterDocumentGraph = {
    schema_version: 'okra-document-graph/v1',
    source: { id: 'src_contract', kind: 'local_upload' },
    document: {
      id: 'doc_contract',
      source_id: 'src_contract',
      mime_type: 'application/pdf',
      lifecycle_state: 'ready',
      permissions: { access: 'private' },
    },
    pages: [],
    blocks: [],
    artifacts: [],
    findings: [],
    redactions: [],
    reviews: [],
    lineage: [],
  };
  const pdf = new Uint8Array([0x25, 0x50, 0x44, 0x46]);

  const program = Effect.gen(function* () {
    const store = yield* ServeStoreService;
    yield* store.ready();
    assertContract(
      (yield* store.readGraph(graph.document.id)) === null,
      `${name}: missing graph is null`,
    );
    yield* store.writeGraph(graph);
    assertContract(
      (yield* store.readGraph(graph.document.id))?.document.id === graph.document.id,
      `${name}: written graph must round-trip`,
    );
    const listed = yield* store.listGraphs();
    assertContract(listed.length === 1, `${name}: listGraphs must include the written graph`);
    yield* store.writeOriginal(graph.document.id, pdf);
    const bytes = yield* store.readOriginal(graph.document.id);
    assertContract(
      bytes !== null && bytes.byteLength === pdf.byteLength,
      `${name}: original PDF must round-trip`,
    );
    const materialized = yield* store.withMaterializedOriginal(graph.document.id, (original) =>
      Effect.succeed(original),
    );
    assertContract(
      materialized.documentId.length > 0 && materialized.dataDir.length > 0,
      `${name}: original materialization must identify a document and data directory`,
    );
    if (store.acquireLease && store.releaseLease) {
      assertContract(
        yield* store.acquireLease(graph.document.id, 'contract-instance', 30_000),
        `${name}: first lease acquisition must succeed`,
      );
      yield* store.releaseLease(graph.document.id, 'contract-instance');
    }
    yield* store.close();
  }).pipe(Effect.provide(layer));

  await Effect.runPromise(program);
}
